import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';

/**
 * TDATeamAssembly — Team creation UI for Team Dynamics Alignment.
 * Leader enters team name, adds member emails (2-12), system validates
 * each email has a completed PASS assessment.
 *
 * Requires tda_purchased = true. Redirects to portal if not purchased.
 *
 * Flow:
 *   1. Enter team name
 *   2. Add member emails (2-12)
 *   3. Click "Validate Team" — calls tda-validate-team serverless function
 *   4. See green/red status for each member
 *   5. When all members valid → "Create Team" button enabled
 *   6. Create team → redirects to leader questionnaire (Increment 4)
 */
function TDATeamAssembly() {
  const { user, profile, loading: authLoading, signOut } = useAuth();

  // Team state
  const [teamName, setTeamName] = useState('');
  const [members, setMembers] = useState([
    { email: '', name: '', role: '' },
    { email: '', name: '', role: '' },
  ]);
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(false);
  const [createdTeamId, setCreatedTeamId] = useState(null);

  // Existing teams for this user
  const [existingTeams, setExistingTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);

  // Redirect if not purchased
  useEffect(() => {
    if (!authLoading && profile && profile.tda_purchased !== true) {
      window.location.href = '/results';
    }
  }, [authLoading, profile]);

  // Fetch existing teams
  useEffect(() => {
    if (!user || authLoading) return;

    const fetchTeams = async () => {
      const { data, error: fetchError } = await supabase
        .from('tda_teams')
        .select('id, team_name, report_status, created_at')
        .eq('leader_user_id', user.id)
        .order('created_at', { ascending: false });

      if (!fetchError && data) {
        setExistingTeams(data);
      }
      setTeamsLoaded(true);
    };

    fetchTeams();
  }, [user, authLoading]);

  // Add a member row
  const addMember = () => {
    if (members.length >= 12) return;
    setMembers([...members, { email: '', name: '', role: '' }]);
    setValidationResults(null);
  };

  // Remove a member row
  const removeMember = (index) => {
    if (members.length <= 2) return;
    const updated = members.filter((_, i) => i !== index);
    setMembers(updated);
    setValidationResults(null);
  };

  // Update a member field
  const updateMember = (index, field, value) => {
    const updated = [...members];
    updated[index] = { ...updated[index], [field]: value };
    setMembers(updated);
    // Clear validation when emails change
    if (field === 'email') {
      setValidationResults(null);
    }
  };

  // Validate all member emails
  const handleValidate = async () => {
    setError('');
    setValidating(true);
    setValidationResults(null);

    // Filter out empty emails
    const filledMembers = members.filter(m => m.email.trim().length > 0);

    if (filledMembers.length < 2) {
      setError('Please enter at least 2 team member email addresses.');
      setValidating(false);
      return;
    }

    // Check for duplicates
    const emails = filledMembers.map(m => m.email.trim().toLowerCase());
    const uniqueEmails = new Set(emails);
    if (uniqueEmails.size !== emails.length) {
      setError('Duplicate email addresses found. Each team member must have a unique email.');
      setValidating(false);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You are not logged in. Please sign in again.');
        setValidating(false);
        return;
      }

      const response = await fetch('/.netlify/functions/tda-validate-team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'validate',
          emails: emails,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to validate team members.');
        setValidating(false);
        return;
      }

      setValidationResults(data.results);
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    }

    setValidating(false);
  };

  // Create the team
  const handleCreateTeam = async () => {
    setError('');
    setCreating(true);

    if (!teamName.trim()) {
      setError('Please enter a team name.');
      setCreating(false);
      return;
    }

    const filledMembers = members.filter(m => m.email.trim().length > 0);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('You are not logged in. Please sign in again.');
        setCreating(false);
        return;
      }

      const response = await fetch('/.netlify/functions/tda-validate-team', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'create_team',
          team_name: teamName.trim(),
          members: filledMembers.map(m => ({
            email: m.email.trim(),
            name: m.name.trim() || null,
            role: m.role.trim() || null,
          })),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to create team.');
        setCreating(false);
        return;
      }

      setCreated(true);
      setCreatedTeamId(data.team_id);

      // Redirect to leader questionnaire after brief confirmation
      setTimeout(() => {
        window.location.href = `/results/tda/questionnaire/${data.team_id}`;
      }, 3000);
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
      setCreating(false);
    }
  };

  // Check if all validated members have completed PASS
  const allMembersValid = validationResults &&
    validationResults.length >= 2 &&
    validationResults.every(r => r.status === 'pass_complete');

  // Check if validation is stale (emails changed since last validation)
  const filledEmails = members.filter(m => m.email.trim().length > 0).map(m => m.email.trim().toLowerCase());
  const validatedEmails = validationResults ? validationResults.map(r => r.email) : [];
  const validationCurrent = validationResults &&
    filledEmails.length === validatedEmails.length &&
    filledEmails.every(e => validatedEmails.includes(e));

  if (authLoading) {
    return (
      <div style={styles.loadingContainer}>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Success state after team creation
  if (created) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <div style={styles.headerInner}>
            <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          </div>
        </header>
        <main style={styles.main}>
          <div style={styles.successCard}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#10003;</div>
            <h2 style={styles.successTitle}>Team Created!</h2>
            <p style={styles.successText}>
              Your team "{teamName}" has been created with {filledEmails.length} members.
              Next, tell us about your team so we can personalize the report.
            </p>
            <p style={styles.successSub}>Redirecting to Team Context form...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <img src="/pass-logo.png" alt="PASS Profile 3.0" style={styles.logo} />
          <button onClick={signOut} style={styles.signOutBtn}>Sign Out</button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.container}>
          {/* Intro banner */}
          <div style={styles.introBanner}>
            <h1 style={styles.introTitle}>Team Dynamics Alignment</h1>
            <p style={styles.introSubtitle}>
              Build your team roster below. Every team member must have a completed
              PASS Profile assessment. We'll validate each member before you proceed.
              When the report is ready, you and each member will receive a copy by email.
            </p>
          </div>

          {/* Existing teams */}
          {teamsLoaded && existingTeams.length > 0 && (
            <div style={styles.existingTeamsBox}>
              <h3 style={styles.existingTeamsTitle}>Your Teams</h3>
              {existingTeams.map(team => (
                <div key={team.id} style={styles.existingTeamRow}>
                  <span style={styles.existingTeamName}>{team.team_name}</span>
                  <span style={styles.existingTeamStatus}>
                    {team.report_status === 'completed' ? 'Report Ready' :
                     team.report_status === 'generating' ? 'Generating...' :
                     'Draft'}
                  </span>
                </div>
              ))}
              <div style={styles.existingTeamsDivider}></div>
              <p style={styles.existingTeamsHint}>Create a new team below, or return to your portal.</p>
            </div>
          )}

          {/* Form */}
          <div style={styles.form}>
            {error && <div style={styles.errorBox}>{error}</div>}

            {/* Team name */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Team Name <span style={styles.required}>*</span>
              </label>
              <p style={styles.fieldHint}>
                A name for this team analysis (e.g., "Marketing Leadership Team", "Project Alpha")
              </p>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder='e.g., "Marketing Leadership Team"'
                style={styles.input}
                maxLength={200}
              />
            </div>

            {/* Members */}
            <div style={styles.membersSection}>
              <h3 style={styles.membersSectionTitle}>
                Team Members ({filledEmails.length} of 2-12)
              </h3>
              <p style={styles.fieldHint}>
                Enter the email address each team member used for their PASS assessment.
                Name and role are optional but help personalize the report.
              </p>

              {members.map((member, index) => (
                <div key={index} style={styles.memberRow}>
                  <div style={styles.memberNumber}>{index + 1}</div>
                  <div style={styles.memberFields}>
                    <input
                      type="email"
                      value={member.email}
                      onChange={(e) => updateMember(index, 'email', e.target.value)}
                      placeholder="Email address *"
                      style={styles.memberInput}
                    />
                    <input
                      type="text"
                      value={member.name}
                      onChange={(e) => updateMember(index, 'name', e.target.value)}
                      placeholder="Name (optional)"
                      style={styles.memberInputSmall}
                    />
                    <input
                      type="text"
                      value={member.role}
                      onChange={(e) => updateMember(index, 'role', e.target.value)}
                      placeholder="Role on team (optional)"
                      style={styles.memberInputSmall}
                    />
                  </div>

                  {/* Validation indicator */}
                  {validationCurrent && validationResults && (
                    <div style={styles.validationIndicator}>
                      {(() => {
                        const result = validationResults.find(
                          r => r.email === member.email.trim().toLowerCase()
                        );
                        if (!result) return null;
                        if (result.status === 'pass_complete') {
                          return <span style={styles.statusGreen} title="PASS assessment complete">&#10004;</span>;
                        }
                        if (result.status === 'pass_incomplete') {
                          return <span style={styles.statusYellow} title="Account exists but PASS not completed">&#9888;</span>;
                        }
                        if (result.status === 'no_account') {
                          return <span style={styles.statusRed} title="No account found with this email">&#10008;</span>;
                        }
                        return null;
                      })()}
                    </div>
                  )}

                  {/* Remove button */}
                  {members.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeMember(index)}
                      style={styles.removeMemberBtn}
                      title="Remove member"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}

              {/* Validation legend */}
              {validationCurrent && validationResults && (
                <div style={styles.legendBox}>
                  <span style={styles.legendItem}>
                    <span style={styles.statusGreen}>&#10004;</span> PASS complete
                  </span>
                  <span style={styles.legendItem}>
                    <span style={styles.statusYellow}>&#9888;</span> PASS not yet completed
                  </span>
                  <span style={styles.legendItem}>
                    <span style={styles.statusRed}>&#10008;</span> No account found
                  </span>
                </div>
              )}

              {/* Add member button */}
              {members.length < 12 && (
                <button
                  type="button"
                  onClick={addMember}
                  style={styles.addMemberBtn}
                >
                  + Add Team Member
                </button>
              )}
            </div>

            {/* Action buttons */}
            <div style={styles.formActions}>
              <a href="/results" style={styles.cancelLink}>Back to Portal</a>
              <div style={styles.actionButtons}>
                {/* Validate button */}
                <button
                  type="button"
                  onClick={handleValidate}
                  disabled={validating || filledEmails.length < 2}
                  style={{
                    ...styles.validateBtn,
                    ...((validating || filledEmails.length < 2) ? styles.btnDisabled : {}),
                  }}
                >
                  {validating ? 'Validating...' : 'Validate Team'}
                </button>

                {/* Create Team button — only enabled when all valid */}
                {validationCurrent && allMembersValid && teamName.trim() && (
                  <button
                    type="button"
                    onClick={handleCreateTeam}
                    disabled={creating}
                    style={{
                      ...styles.createBtn,
                      ...(creating ? styles.btnDisabled : {}),
                    }}
                  >
                    {creating ? 'Creating Team...' : 'Create Team'}
                  </button>
                )}
              </div>
            </div>

            {/* Validation summary message */}
            {validationCurrent && validationResults && !allMembersValid && (
              <div style={styles.validationSummary}>
                <p style={styles.validationSummaryText}>
                  {validationResults.filter(r => r.status !== 'pass_complete').length} member(s)
                  do not have a completed PASS assessment. All team members must complete PASS
                  before you can create a TDA team.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

/* ===== STYLES ===== */
const styles = {
  page: {
    minHeight: '100vh',
    background: '#F8F9FA',
    fontFamily: 'Inter, system-ui, sans-serif',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#F8F9FA',
  },
  loadingText: {
    color: '#6B7A8F',
    fontSize: 16,
  },
  header: {
    backgroundColor: '#FFFFFF',
    borderBottom: '3px solid #2A9D8F',
    padding: '16px 24px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  headerInner: {
    maxWidth: 1100,
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    height: '40px',
  },
  signOutBtn: {
    background: 'transparent',
    border: '1px solid #0B3D2E',
    color: '#0B3D2E',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
  },
  main: {
    flex: 1,
    padding: '32px 0',
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '0 24px',
  },

  // Intro banner
  introBanner: {
    background: 'linear-gradient(135deg, #0B3D2E 0%, #2A9D8F 100%)',
    borderRadius: 12,
    padding: '32px 40px',
    marginBottom: 32,
    textAlign: 'center',
  },
  introTitle: {
    color: 'white',
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 12px 0',
    fontFamily: 'Outfit, Inter, sans-serif',
  },
  introSubtitle: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 15,
    lineHeight: 1.6,
    margin: 0,
  },

  // Existing teams
  existingTeamsBox: {
    background: 'white',
    borderRadius: 12,
    padding: '24px 28px',
    marginBottom: 24,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
  },
  existingTeamsTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 12px 0',
  },
  existingTeamRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #F0F0F0',
  },
  existingTeamName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
  },
  existingTeamStatus: {
    fontSize: 13,
    color: '#6B7A8F',
  },
  existingTeamsDivider: {
    height: 1,
    background: '#E8ECF0',
    margin: '12px 0',
  },
  existingTeamsHint: {
    fontSize: 13,
    color: '#6B7A8F',
    margin: 0,
  },

  // Form
  form: {
    background: 'white',
    borderRadius: 12,
    padding: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
  },
  errorBox: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '12px 16px',
    color: '#DC2626',
    fontSize: 14,
    marginBottom: 24,
  },
  fieldGroup: {
    marginBottom: 24,
  },
  label: {
    display: 'block',
    fontSize: 15,
    fontWeight: 600,
    color: '#0B3D2E',
    marginBottom: 6,
  },
  required: {
    color: '#DC2626',
  },
  fieldHint: {
    fontSize: 13,
    color: '#6B7A8F',
    margin: '0 0 8px 0',
    lineHeight: 1.4,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #D1D5DB',
    borderRadius: 8,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  },

  // Members section
  membersSection: {
    marginBottom: 24,
  },
  membersSectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 4px 0',
  },
  memberRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 12,
    padding: '12px 14px',
    background: '#FAFBFC',
    borderRadius: 8,
    border: '1px solid #E8ECF0',
  },
  memberNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: '#E0F7FA',
    color: '#0B3D2E',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 4,
  },
  memberFields: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  memberInput: {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  },
  memberInputSmall: {
    width: '100%',
    padding: '8px 12px',
    fontSize: 13,
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    fontFamily: 'Inter, system-ui, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#555',
  },
  validationIndicator: {
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 4,
    fontSize: 20,
  },
  statusGreen: {
    color: '#16A34A',
    fontSize: 20,
    fontWeight: 700,
  },
  statusYellow: {
    color: '#D97706',
    fontSize: 20,
  },
  statusRed: {
    color: '#DC2626',
    fontSize: 20,
    fontWeight: 700,
  },
  removeMemberBtn: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1px solid #E0E0E0',
    background: 'white',
    color: '#999',
    fontSize: 18,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 4,
    lineHeight: 1,
  },

  // Legend
  legendBox: {
    display: 'flex',
    gap: 20,
    padding: '10px 14px',
    background: '#F8F9FA',
    borderRadius: 6,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  legendItem: {
    fontSize: 12,
    color: '#6B7A8F',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },

  // Add member button
  addMemberBtn: {
    background: 'transparent',
    border: '1px dashed #2A9D8F',
    color: '#2A9D8F',
    padding: '10px 20px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    width: '100%',
    fontFamily: 'Inter, system-ui, sans-serif',
  },

  // Form actions
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 32,
    paddingTop: 24,
    borderTop: '1px solid #E8ECF0',
  },
  cancelLink: {
    color: '#6B7A8F',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
  },
  actionButtons: {
    display: 'flex',
    gap: 12,
  },
  validateBtn: {
    background: 'transparent',
    color: '#0B3D2E',
    padding: '12px 24px',
    borderRadius: 8,
    border: '1px solid #0B3D2E',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  createBtn: {
    background: '#2A9D8F',
    color: 'white',
    padding: '12px 24px',
    borderRadius: 8,
    border: 'none',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  // Validation summary
  validationSummary: {
    marginTop: 16,
    padding: '12px 16px',
    background: '#FEF9EC',
    border: '1px solid #FDE68A',
    borderRadius: 8,
  },
  validationSummaryText: {
    fontSize: 13,
    color: '#92400E',
    margin: 0,
    lineHeight: 1.5,
  },

  // Success state
  successCard: {
    background: 'white',
    borderRadius: 12,
    padding: '48px 40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #E8ECF0',
    textAlign: 'center',
    maxWidth: 600,
    margin: '64px auto',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#0B3D2E',
    margin: '0 0 12px 0',
  },
  successText: {
    fontSize: 15,
    color: '#6B7A8F',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  successSub: {
    fontSize: 13,
    color: '#6B7A8F',
    fontStyle: 'italic',
  },
};

export default TDATeamAssembly;
